import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Badge, Can, ConfirmDialog, Drawer, Field, JsonView, PromptDialog, formatDateTime, notify, type BadgeTone } from '@/kit/ui'
import { TEST_APPLICANTS } from '@/fixtures/flows'
import { compareVersions } from '@/lib/batch'
import { ENDPOINT_STATE_LABEL, batchOf, effectiveState, switchable, type EndpointState } from '@/lib/flow'
import { invokeFor } from '@/lib/sandbox'
import { apiMode } from '@/lib/hubApi'
import { testCallOnHub } from '@/lib/flowApi'
import { testCall, type TestCallResult } from '@/lib/testCall'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import { useFlowStore } from '@/store/flowStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Lender, ServiceEndpoint } from '@/types'
import { useSandbox } from '../useSandbox'
import { ApplicantPicker, ReplyPicker, endpointsOf } from './RunInputs'

export const STATE_TONE: Record<EndpointState, BadgeTone> = { live: 'success', test: 'violet', suspended: 'danger', lender_off: 'danger', product_inactive: 'warning', retired: 'gray' }
export const EndpointStateBadge = ({ state }: { state: EndpointState }) => <Badge tone={STATE_TONE[state]}>{ENDPOINT_STATE_LABEL[state]}</Badge>
const shortName = (n: string) => n.split('.').slice(1).join('.')
const secs = (ms: number | null) => (ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`)

/** 3.5 Service endpoints (F3-3): what the web app calls; one workflow version behind each; switch, suspend, retire, test call. */
export function EndpointsSubTab({ lender }: { lender: Lender }) {
  const [params, setParams] = useSearchParams()
  const { user, can } = useCurrentUser()
  const endpoints = useFlowStore((s) => s.endpoints)
  const flows = useFlowStore((s) => s.flows)
  const batches = useCodeStore((s) => s.batches)
  const rule = useSettingsStore((s) => s.settings.errorRule)
  const fs = useFlowStore.getState

  const mine = useMemo(() => endpoints.filter((e) => e.lenderSlug === lender.slug), [endpoints, lender.slug])
  const ep = mine.find((e) => e.id === params.get('ep')) ?? mine.find((e) => e.state !== 'retired') ?? mine[0]
  const product = (e: ServiceEndpoint) => lender.products.find((p) => p.slug === e.productSlug)
  const stateOf = (e: ServiceEndpoint) => effectiveState(e, lender, product(e))
  const flowOf = (e: ServiceEndpoint) => flows.find((f) => f.name === e.workflowName && f.version === e.workflowVersion)

  const batchBehind = ep ? (flowOf(ep) ? batchOf(flowOf(ep)!, batches) : undefined) : undefined
  // the sandbox of the batch behind the endpoint, opened when a test call needs it
  const sb = useSandbox(lender, batchBehind, { auto: false })
  const [target, setTarget] = useState('')
  const [applicantId, setApplicantId] = useState('A')
  const [replies, setReplies] = useState<Record<string, string>>({})
  const [call, setCall] = useState<TestCallResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [suspending, setSuspending] = useState(false)
  const [retiring, setRetiring] = useState(false)
  const [creating, setCreating] = useState(false)
  useEffect(() => {
    setTarget(ep?.workflowVersion ?? '')
    setCall(null)
    setReplies({})
  }, [ep?.id, ep?.workflowVersion])

  const act = (fn: () => void, ok: string, detail?: string) => {
    try {
      fn()
      notify.success(ok, detail)
    } catch (err) {
      notify.error('Refused', err instanceof Error ? err.message : String(err))
    }
  }

  const send = async () => {
    if (!ep) return
    setBusy(true)
    try {
      if (apiMode()) {
        // the Hub runs it on the runner's UAT container, records it, captures the codes and applies the error rule
        const r = await testCallOnHub(ep.id, TEST_APPLICANTS.find((a) => a.id === applicantId)!.data, replies)
        setCall(r)
        if (r.run) {
          fs().recordRun(r.run)
          useCodeStore.getState().captureCodes(lender.slug, [], 'uat')
          if (r.fresh.length) notify.warning(`New lender code ${r.fresh.join(', ')}`, 'Captured in 3.1 with an alert: parse() returns error for it.')
        }
        return
      }
      if (batchBehind && sb.status !== 'ready') {
        const info = await sb.open()
        if (!info) { notify.error('Sandbox not ready', sb.error ?? 'The playground agent is not running: a test call runs the endpoints in the batch sandbox.'); return }
      }
      const r = await testCall(ep, lender, flows, batches, TEST_APPLICANTS.find((a) => a.id === applicantId)!.data, replies, batchBehind ? invokeFor(lender, batchBehind) : async () => ({ ok: false, outcome: null, error: 'no batch', issues: [], calls: [], log: [], envMissing: [], ms: 0 }))
      setCall(r)
      if (r.run) {
        fs().recordRun(r.run)
        const fresh = useCodeStore.getState().captureCodes(lender.slug, r.result!.codes, 'uat')
        if (fresh.length) notify.warning(`New lender code ${fresh.join(', ')}`, 'Captured in 3.1 with an alert: parse() returns error for it.')
      }
    } catch (err) {
      notify.error('Test call refused', err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const versions = ep ? flows.filter((f) => f.name === ep.workflowName).sort((a, b) => compareVersions(b.version, a.version)) : []
  const behindFlow = ep ? flowOf(ep) : undefined

  return (
    <>
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted">An endpoint is what the web app calls (<code>POST /v1/applications</code> with <code>endpoint_id</code>). Behind it: exactly one workflow version, which uses one API batch. Switching the version is one click; the app notices nothing.</p>
        <Can permission="gateway.manage"><button type="button" className="btn btn-primary" onClick={() => setCreating(true)} data-testid="create-endpoint-open">+ Create endpoint</button></Can>
      </div>
      <div className="card-flush overflow-x-auto">
        <table className="table">
          <thead><tr><th>endpoint_id</th><th>Product</th><th>Workflow</th><th>Batch</th><th>Env</th><th>State</th><th className="text-right">Runs 24 h</th><th className="text-right">Avg</th><th className="text-right">Errors</th></tr></thead>
          <tbody>
            {mine.map((e) => {
              const f = flowOf(e)
              return (
                <tr key={e.id} className={`cursor-pointer ${e.id === ep?.id ? 'bg-primary-soft/40' : ''}`} onClick={() => setParams((p) => (p.set('ep', e.id), p))} data-testid={`ep-row-${e.id}`}>
                  <td className="mono"><b>{e.id}</b></td>
                  <td>{product(e)?.name ?? e.productSlug}</td>
                  <td className="mono">{shortName(e.workflowName)} {e.workflowVersion}</td>
                  <td className="mono">{f ? batchOf(f, batches)?.version ?? '—' : '—'}</td>
                  <td>{e.env === 'uat' ? 'UAT only' : 'UAT + Prod'}</td>
                  <td><EndpointStateBadge state={stateOf(e)} /></td>
                  <td className="text-right">{e.stats.runs24h}</td>
                  <td className="text-right">{secs(e.stats.avgMs)}</td>
                  <td className={`text-right ${e.stats.errors24h ? 'text-red-700 dark:text-red-300' : ''}`}>{e.stats.errors24h}</td>
                </tr>
              )
            })}
            {mine.length === 0 && <tr><td colSpan={9} className="text-center text-muted">No endpoint yet. A workflow version goes live behind an endpoint: + Create endpoint.</td></tr>}
          </tbody>
        </table>
      </div>

      {ep && (
        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <section className="card" data-testid="ep-card">
            <h2 className="section-title flex items-center gap-2">{ep.id} <EndpointStateBadge state={stateOf(ep)} /></h2>
            <label className="label" htmlFor="ep-ver">Workflow version behind this endpoint</label>
            <select id="ep-ver" className="input mono" value={target} onChange={(e) => setTarget(e.target.value)} disabled={ep.state === 'retired'}>
              {versions.map((f) => {
                const s = switchable(ep, f, batches)
                return <option key={f.id} value={f.version} disabled={!s.ok && f.version !== ep.workflowVersion}>{shortName(f.name)} {f.version} ({f.status.replace('_', ' ')}){f.version === ep.workflowVersion ? ' ← current' : !s.ok ? `: ${s.why}` : ''}</option>
              })}
            </select>
            <div className="my-2.5 flex flex-wrap items-center gap-1.5">
              <Can permission="code.publish">
                <button type="button" className="btn btn-primary btn-sm" disabled={target === ep.workflowVersion || ep.state === 'retired'} onClick={() => act(() => fs().switchEndpoint(ep.id, target, user.email), `Switched to ${shortName(ep.workflowName)} ${target}`, ep.env === 'uat' ? 'UAT only endpoint.' : `${ep.workflowVersion} is previous; switch back any time.`)} data-testid="ep-switch">Switch version</button>
              </Can>
              <Can permission="gateway.manage">
                {ep.state === 'suspended'
                  ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => act(() => fs().setEndpointState(ep.id, 'active', null, user.email), `${ep.id} resumed`)} data-testid="ep-resume">Resume</button>
                  : <button type="button" className="btn btn-secondary btn-sm" disabled={ep.state === 'retired'} onClick={() => setSuspending(true)} data-testid="ep-suspend">Suspend</button>}
                <button type="button" className="btn btn-secondary btn-sm" disabled={ep.state === 'retired'} onClick={() => setRetiring(true)}>Retire</button>
              </Can>
              <span className="text-2xs text-muted">{can('code.publish') ? (ep.env === 'uat' ? 'UAT only: drafts allowed' : 'production: paths passed and a built batch only') : 'admins switch, suspend and retire'}</span>
            </div>
            <dl className="grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 text-xs">
              <dt className="text-muted">Runs</dt><dd className="mono">{behindFlow ? `${shortName(behindFlow.name)} ${behindFlow.version} · batch ${batchOf(behindFlow, batches)?.version ?? '—'}` : 'missing'}</dd>
              <dt className="text-muted">Key for the app</dt><dd>HUB_APP_KEY · the lender's exact reply <b>masked</b> in the answer, full in the Gateway log</dd>
              <dt className="text-muted">Alerts</dt><dd>&gt; {rule.errors} errors / {rule.minutes} min → Alerts (now {ep.stats.errorsWindow} in the window)</dd>
              <dt className="text-muted">Last switch</dt><dd>{ep.lastSwitch ? `${formatDateTime(ep.lastSwitch.at)} · ${ep.lastSwitch.from} → ${ep.lastSwitch.to} · ${ep.lastSwitch.by.split('@')[0]}` : '—'}</dd>
              {ep.suspendReason && <><dt className="text-muted">Suspended</dt><dd>“{ep.suspendReason}”</dd></>}
              {lender.status !== 'active' && <><dt className="text-muted">Lender</dt><dd className="text-red-700 dark:text-red-300">Off: every endpoint answers 409 until the lender is switched on (tab header)</dd></>}
            </dl>
          </section>

          <section className="card" data-testid="ep-test">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h2 className="section-title !mb-0">Test call (UAT)</h2>
              <Can permission="code.test"><button type="button" className="btn btn-primary btn-sm" disabled={busy || sb.status === 'opening'} onClick={send} data-testid="ep-test-call">{sb.status === 'opening' ? 'Starting the sandbox…' : busy && apiMode() ? 'Calling the Hub…' : 'Send test call'}</button></Can>
            </div>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <ApplicantPicker value={applicantId} onChange={setApplicantId} id="ep-app" />
              <div className="sm:col-span-2"><ReplyPicker batch={behindFlow ? batchOf(behindFlow, batches) : undefined} routes={behindFlow ? endpointsOf(behindFlow.nodes) : []} value={replies} onChange={setReplies} /></div>
            </div>
            {call && (
              <div className="mt-2 space-y-2" data-testid="ep-test-result">
                <div className="text-2xs font-semibold">{call.request.method} {call.request.path} (x-app-key: ••••)</div>
                <JsonView value={call.request.body} maxHeight={160} />
                <div className="text-2xs font-semibold">→ {call.httpStatus}{call.result ? ` · ${(call.result.ms / 1000).toFixed(2)} s · ${shortName(ep.workflowName)} ${call.run?.workflowVersion} · batchApi ${call.run?.batchVersion}` : ''}</div>
                <JsonView value={call.response} maxHeight={240} />
                {call.run && <p className="text-2xs text-muted">In <b>Workflow runs</b> as {call.run.appRef} · log {call.run.logId}. The PI is masked; the lender's exact reply stays in the Gateway log.</p>}
              </div>
            )}
          </section>
        </div>
      )}

      <PromptDialog
        isOpen={suspending}
        onClose={() => setSuspending(false)}
        title={`Suspend ${ep?.id ?? ''}?`}
        message="The web app gets “lender unavailable” for this endpoint until it is resumed. The lender and its other endpoints stay as they are."
        inputLabel="Reason (History and Alerts)"
        validate={(v) => (v.trim().length >= 5 ? null : 'At least 5 characters')}
        confirmText="Suspend"
        onConfirm={(reason) => { if (ep) act(() => fs().setEndpointState(ep.id, 'suspended', reason.trim(), user.email), `${ep.id} suspended`); setSuspending(false) }}
      />
      <ConfirmDialog
        isOpen={retiring}
        onClose={() => setRetiring(false)}
        type="danger"
        title={`Retire ${ep?.id ?? ''}?`}
        message="For good: the endpoint_id stops answering (404) and cannot be resumed. The workflow versions stay. The app must be moved to another endpoint first."
        confirmText="Retire"
        onConfirm={() => { if (ep) act(() => fs().setEndpointState(ep.id, 'retired', null, user.email), `${ep.id} retired`); setRetiring(false) }}
      />
      <CreateEndpointDrawer lender={lender} isOpen={creating} onClose={() => setCreating(false)} onCreated={(id) => setParams((p) => (p.set('ep', id), p))} />
    </>
  )
}

function CreateEndpointDrawer({ lender, isOpen, onClose, onCreated }: { lender: Lender; isOpen: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { user } = useCurrentUser()
  const flows = useFlowStore((s) => s.flows)
  const batches = useCodeStore((s) => s.batches)
  const names = Array.from(new Set(flows.filter((f) => f.lenderSlug === lender.slug).map((f) => f.name)))
  const [name, setName] = useState('')
  const [version, setVersion] = useState('')
  const [env, setEnv] = useState<ServiceEndpoint['env']>('uat')
  const [id, setId] = useState('')
  const [error, setError] = useState<string>()
  useEffect(() => {
    if (!isOpen) return
    const n = names[0] ?? ''
    setName(n)
    setVersion(flows.find((f) => f.name === n && f.status !== 'retired')?.version ?? '')
    setEnv('uat')
    setId(`${lender.slug}.${(n.split('.')[1] ?? 'flow')}-uat2`)
    setError(undefined)
  }, [isOpen]) // eslint-disable-line react-hooks/exhaustive-deps
  const choices = flows.filter((f) => f.name === name && f.status !== 'retired')
  const flow = choices.find((f) => f.version === version)
  const submit = () => {
    try {
      if (!flow) throw new Error('Pick a workflow version')
      const draftEp: ServiceEndpoint = { id, lenderSlug: lender.slug, productSlug: flow.productSlug, workflowName: name, workflowVersion: version, env, state: 'active', suspendReason: null, stats: { runs24h: 0, avgMs: null, errors24h: 0, errorsWindow: 0 }, lastSwitch: null, createdAt: '' }
      const s = switchable(draftEp, flow, batches)
      if (!s.ok) throw new Error(`${name} ${version} cannot run on a production endpoint: ${s.why}`)
      useFlowStore.getState().createEndpoint({ id, lenderSlug: lender.slug, productSlug: flow.productSlug, workflowName: name, workflowVersion: version, env }, user.email)
      notify.success(`${id} created`, env === 'uat' ? 'UAT only: for testing before go-live.' : 'UAT + production: the app can call it once the product is active.')
      onClose()
      onCreated(id)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }
  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Create endpoint" subtitle={`${lender.name} · one workflow version behind it`} footer={<div className="flex justify-end gap-2"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="button" className="btn btn-primary" onClick={submit} data-testid="create-endpoint">Create endpoint</button></div>}>
      <Field label="endpoint_id" required help="What the app sends; lender.product, e.g. moneyview.personal-uat">
        {(fid) => <input id={fid} className="input mono" value={id} onChange={(e) => setId(e.target.value.trim())} />}
      </Field>
      <Field label="Workflow" required>
        {(fid) => <select id={fid} className="input mono" value={name} onChange={(e) => { setName(e.target.value); setVersion(flows.find((f) => f.name === e.target.value && f.status !== 'retired')?.version ?? '') }}>{names.map((n) => <option key={n}>{n}</option>)}</select>}
      </Field>
      <Field label="Version" required>
        {(fid) => <select id={fid} className="input mono" value={version} onChange={(e) => setVersion(e.target.value)}>{choices.map((f) => <option key={f.id} value={f.version}>{f.version} ({f.status.replace('_', ' ')})</option>)}</select>}
      </Field>
      <Field label="Environment" required>
        {(fid) => <select id={fid} className="input" value={env} onChange={(e) => setEnv(e.target.value as ServiceEndpoint['env'])}><option value="uat">UAT only (testing, drafts allowed)</option><option value="uat_prod">UAT + production</option></select>}
      </Field>
      {error && <p className="field-error">{error}</p>}
    </Drawer>
  )
}
